// Car Tier System - Manages car quality tiers based on miles invested

import {
    CAR_TIER_BALANCE,
    CAR_TIER_SCALING as BALANCE_CAR_TIER_SCALING,
    CAR_UPGRADE_COST_MULTIPLIERS as BALANCE_CAR_UPGRADE_COST_MULTIPLIERS
} from '../config/balance.js';

// Re-export from balance.js for backward compatibility
// These are the single source of truth from balance.js
export const TIERS = CAR_TIER_BALANCE.TIER_ORDER;
export const BASE_TIER_THRESHOLDS = CAR_TIER_BALANCE.BASE_TIER_THRESHOLDS;
export const TIER_STAT_MULTIPLIERS = CAR_TIER_BALANCE.TIER_STAT_MULTIPLIERS;
export const TIER_TRANSFER_RATES = CAR_TIER_BALANCE.TIER_TRANSFER_RATES;
export const TIER_COLORS = CAR_TIER_BALANCE.TIER_COLORS;
export const ASCENSION_LOSS = CAR_TIER_BALANCE.ASCENSION_LOSS;
export const MAX_UPGRADE_LEVEL = CAR_TIER_BALANCE.MAX_UPGRADE_LEVEL;
export const CAR_TIER_SCALING = BALANCE_CAR_TIER_SCALING;

/**
 * Calculate the tier for a car based on miles invested
 * @param {number} milesInvested - Total miles invested in this car's upgrades
 * @param {string} carId - The car identifier (starter, sedan, etc.)
 * @returns {string} The tier name (faded, standard, pristine, golden, vectorized)
 */
export function calculateTier(milesInvested, carId) {
    const scaling = CAR_TIER_SCALING[carId] || 1.0;

    // Check thresholds from highest to lowest
    for (let i = TIERS.length - 1; i >= 0; i--) {
        const threshold = BASE_TIER_THRESHOLDS[i] * scaling;
        if (milesInvested >= threshold) {
            return TIERS[i];
        }
    }

    return TIERS[0]; // Default to faded
}

/**
 * Get the tier index (0-4) for a tier name
 * @param {string} tier - The tier name
 * @returns {number} The tier index
 */
export function getTierIndex(tier) {
    return TIERS.indexOf(tier);
}

/**
 * Get progress toward the next tier (0.0 to 1.0)
 * @param {number} milesInvested - Total miles invested in this car's upgrades
 * @param {string} carId - The car identifier
 * @returns {{progress: number, currentTier: string, nextTier: string|null, milesNeeded: number}} Progress info
 */
export function getTierProgress(milesInvested, carId) {
    const scaling = CAR_TIER_SCALING[carId] || 1.0;
    const currentTier = calculateTier(milesInvested, carId);
    const currentIndex = getTierIndex(currentTier);

    // If at max tier, return 100% progress
    if (currentIndex >= TIERS.length - 1) {
        return {
            progress: 1.0,
            currentTier,
            nextTier: null,
            milesNeeded: 0,
            currentThreshold: BASE_TIER_THRESHOLDS[currentIndex] * scaling,
            nextThreshold: null
        };
    }

    const currentThreshold = BASE_TIER_THRESHOLDS[currentIndex] * scaling;
    const nextThreshold = BASE_TIER_THRESHOLDS[currentIndex + 1] * scaling;
    const rangeSize = nextThreshold - currentThreshold;
    const progressInRange = milesInvested - currentThreshold;

    return {
        progress: Math.min(1.0, progressInRange / rangeSize),
        currentTier,
        nextTier: TIERS[currentIndex + 1],
        milesNeeded: Math.max(0, nextThreshold - milesInvested),
        currentThreshold,
        nextThreshold
    };
}

/**
 * Get the upgrade level cap (no longer tier-gated)
 * @returns {number} The max upgrade level allowed
 */
export function getTierUpgradeCap() {
    return MAX_UPGRADE_LEVEL;
}

/**
 * Get the stat multiplier for a tier
 * @param {string} tier - The tier name
 * @returns {number} The stat multiplier (1.0 to 1.3)
 */
export function getTierStatMultiplier(tier) {
    return TIER_STAT_MULTIPLIERS[tier] || 1.0;
}

/**
 * Get the tier color
 * @param {string} tier - The tier name
 * @returns {string} The hex color code
 */
export function getTierColor(tier) {
    return TIER_COLORS[tier] || TIER_COLORS.faded;
}

/**
 * Calculate stat bonus from upgrade level for a specific stat
 * @param {string} statType - The stat type (baseSpeed, acceleration, etc.)
 * @param {number} level - The upgrade level
 * @param {object} carConfig - The car configuration
 * @returns {number} The stat bonus from upgrades (not including base stat)
 */
export function calculateStatBonus(statType, level, carConfig) {
    if (level <= 0) return 0;

    switch (statType) {
        case 'baseSpeed': {
            const increment = (carConfig.maxBaseSpeed - carConfig.baseSpeed) / 10;
            return level * increment;
        }
        case 'acceleration': {
            const increment = (carConfig.maxAcceleration - carConfig.baseAcceleration) / 10;
            return level * increment;
        }
        case 'maxSpeed': {
            const increment = carConfig.maxSpeed / 10;
            return level * increment;
        }
        case 'cruiseDuration': {
            const increment = (carConfig.maxCruiseDuration - carConfig.baseCruiseDuration) / 10;
            return level * increment;
        }
        case 'cruiseCooldown': {
            // Cooldown reduction (negative is better)
            const decrement = (carConfig.baseCruiseCooldown - 10) / 10;
            return -(level * decrement); // Negative because it reduces cooldown
        }
        case 'momentum': {
            const increment = (carConfig.maxMomentumCap - carConfig.baseMomentumCap) / 10;
            return level * increment;
        }
        case 'momentumRate': {
            const baseRate = carConfig.baseMomentumRate || 0.05;
            const maxRate = carConfig.maxMomentumRate || 0.10;
            const increment = (maxRate - baseRate) / 10;
            return level * increment;
        }
        default:
            return 0;
    }
}

/**
 * Calculate what happens when ascending old car into new car
 * @param {object} oldCar - The old car state object (with upgradeLevels, milesInvested)
 * @param {string} oldCarId - The old car ID
 * @param {string} newCarId - The new car ID
 * @param {object} oldCarConfig - The old car's configuration (for stat calculations)
 * @returns {object} Ascension result with transfer details
 */
export function calculateAscension(oldCar, oldCarId, newCarId, oldCarConfig) {
    const oldMilesInvested = oldCar.milesInvested || 0;
    const oldTier = calculateTier(oldMilesInvested, oldCarId);
    const transferRate = TIER_TRANSFER_RATES[oldTier];
    const oldTierMultiplier = getTierStatMultiplier(oldTier);

    // Check if old car has any actual upgrade levels
    const hasAnyUpgrades = oldCar.upgradeLevels &&
        Object.values(oldCar.upgradeLevels).some(level => level > 0);

    // Calculate miles that transfer (minus ascension loss)
    const milesLost = Math.floor(oldMilesInvested * ASCENSION_LOSS);
    let milesTransferred = oldMilesInvested - milesLost;

    // Scale transferred miles based on car tier scaling difference
    // Better cars need more investment, so miles are worth less when ascending up
    const oldScaling = CAR_TIER_SCALING[oldCarId] || 1.0;
    const newScaling = CAR_TIER_SCALING[newCarId] || 1.0;
    if (newScaling > oldScaling) {
        // Ascending to a better car - scale down miles proportionally
        milesTransferred = Math.floor(milesTransferred * (oldScaling / newScaling));
    }
    // Note: If descending to a worse car (unlikely), we don't scale up

    // Calculate what tier the new car will start at
    // If no upgrades on old car, new car starts at Faded regardless of miles
    const newTier = hasAnyUpgrades ? calculateTier(milesTransferred, newCarId) : TIERS[0];
    const newTierCap = getTierUpgradeCap(newTier);

    // Calculate Ascension Bonus (AB) for each stat
    // AB = stat bonus from upgrades * tier multiplier * transfer rate
    // Note: cruiseCooldown is excluded - it doesn't receive AB to keep values predictable
    const ascensionBonus = {
        baseSpeed: 0,
        acceleration: 0,
        maxSpeed: 0,
        cruiseDuration: 0,
        momentum: 0,
        momentumRate: 0
    };

    const statTypes = Object.keys(ascensionBonus);
    for (const statType of statTypes) {
        const level = oldCar.upgradeLevels?.[statType] || 0;
        if (level > 0) {
            const statBonus = calculateStatBonus(statType, level, oldCarConfig);
            // Apply tier multiplier to the bonus, then transfer rate
            const finalBonus = statBonus * oldTierMultiplier * transferRate;
            ascensionBonus[statType] = Math.round(finalBonus * 100) / 100; // Round to 2 decimals
        }
    }

    // Calculate miles before scaling for display
    const milesAfterLoss = oldMilesInvested - milesLost;
    const scalingApplied = newScaling > oldScaling;
    const scalingRatio = scalingApplied ? (oldScaling / newScaling) : 1.0;

    return {
        oldCar: {
            id: oldCarId,
            tier: oldTier,
            milesInvested: oldMilesInvested
        },
        transfer: {
            milesLost,
            milesAfterLoss,
            milesTransferred,
            transferRate,
            transferRatePercent: Math.round(transferRate * 100),
            scalingApplied,
            scalingRatio,
            scalingPercent: scalingApplied ? Math.round(scalingRatio * 100) : 100
        },
        newCar: {
            id: newCarId,
            tier: newTier,
            tierColor: getTierColor(newTier),
            milesInvested: milesTransferred,
            upgradeCap: newTierCap,
            statMultiplier: getTierStatMultiplier(newTier)
        },
        ascensionBonus
    };
}

/**
 * Calculate miles invested from existing upgrade levels (for migration)
 * @param {object} car - The car state object with upgradeLevels
 * @param {string} carId - The car identifier
 * @param {object} upgradesConfig - The upgrades configuration
 * @returns {number} Total miles that would have been spent on these upgrades
 */
export function calculateMilesInvestedFromUpgrades(car, carId, upgradesConfig) {
    let total = 0;

    if (!car.upgradeLevels || !upgradesConfig) {
        return 0;
    }

    const carMultiplier = BALANCE_CAR_UPGRADE_COST_MULTIPLIERS[carId] || 1;

    for (const [upgradeType, level] of Object.entries(car.upgradeLevels)) {
        const upgrade = upgradesConfig[upgradeType];
        if (!upgrade || !upgrade.baseCosts) {
            continue;
        }

        const baseCosts = upgrade.baseCosts;
        for (let i = 0; i < level; i++) {
            if (baseCosts[i] !== undefined) {
                // Apply car multiplier to match actual costs paid
                total += Math.floor(baseCosts[i] * carMultiplier);
            }
        }
    }

    return total;
}

/**
 * Format tier name for display (capitalize first letter)
 * @param {string} tier - The tier name
 * @returns {string} Formatted tier name
 */
export function formatTierName(tier) {
    if (!tier) return 'Faded';
    return tier.charAt(0).toUpperCase() + tier.slice(1);
}
